import { format, startOfMonth, subMonths } from "date-fns";
import { de } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { getFilterDisplayText } from "@/utils/dateFilters";

interface MonthOption {
  value: string;
  label: string;
}

export function useAvailableMonths(targetUserId?: string) {
  const { user } = useAuth();
  const userId = targetUserId || user?.id;

  const { data: availableMonths = [] } = useQuery<MonthOption[]>({
    queryKey: ["available-months", userId],
    queryFn: async () => {
      if (!userId) return [];

      const currentMonthKey = format(new Date(), "yyyy-MM");
      const currentMonth: MonthOption = {
        value: "current",
        label: format(new Date(), "MMM yyyy", { locale: de }),
      };

      const startDate = subMonths(startOfMonth(new Date()), 11).toISOString();

      const { data, error } = await supabase
        .from("time_entries")
        .select("start_time")
        .eq("user_id", userId)
        .is("deleted_at", null)
        .gte("start_time", startDate)
        .order("start_time", { ascending: false });

      if (error) throw error;

      // Deduplicate using Map (value → MonthOption) — no JSON roundtrip
      const seen = new Map<string, MonthOption>();
      if (data) {
        data.forEach((entry) => {
          const monthKey = format(new Date(entry.start_time), "yyyy-MM");
          if (monthKey !== currentMonthKey && !seen.has(monthKey)) {
            seen.set(monthKey, { value: monthKey, label: getFilterDisplayText(monthKey) });
          }
        });
      }

      const monthsArray = [...seen.values()].sort(
        (a, b) => new Date(b.value + "-01").getTime() - new Date(a.value + "-01").getTime()
      );

      return [currentMonth, ...monthsArray];
    },
    enabled: !!userId,
  });

  return availableMonths;
}
